import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Loader2, Pencil, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(v || 0);
// Literal class names so Tailwind keeps them in the build.
const STAGE_CLASSES = {
  green: { box: 'bg-green-50 border-green-100', badge: 'bg-green-500', title: 'text-green-800', text: 'text-green-600', big: 'text-green-700' },
  amber: { box: 'bg-amber-50 border-amber-100', badge: 'bg-amber-500', title: 'text-amber-800', text: 'text-amber-600', big: 'text-amber-700' },
  red: { box: 'bg-red-50 border-red-100', badge: 'bg-red-500', title: 'text-red-800', text: 'text-red-600', big: 'text-red-700' },
};
const pct = (r) => `${+(Number(r || 0) * 100).toFixed(4)}%`;

// IFRS9 inputs (staging thresholds + provision rates) and the loans marked
// in default — editable by the admin, every change in the Audit Trail
// (user 2026-10-21). Rates are stored as decimals and edited as percentages.
export default function Ifrs9SettingsCard({ token, onSettingsChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const [newDefault, setNewDefault] = useState({ loan_number: '', reason: '' });
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/lms/ifrs9-settings`, { headers });
      setSettings(res.data.settings);
      setDefaults(res.data.loans_in_default || []);
      onSettingsChange && onSettingsChange(res.data.settings);
    } catch (err) {
      console.error('Failed to load IFRS9 settings:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setForm({
      stage1_max_days: settings.stage1_max_days,
      stage2_max_days: settings.stage2_max_days,
      stage1_rate: +(settings.stage1_rate * 100).toFixed(4),
      stage2_rate: +(settings.stage2_rate * 100).toFixed(4),
      stage3_rate: +(settings.stage3_rate * 100).toFixed(4),
      default_rate: +(settings.default_rate * 100).toFixed(4),
    });
    setEditing(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/lms/ifrs9-settings`, {
        stage1_max_days: parseInt(form.stage1_max_days, 10),
        stage2_max_days: parseInt(form.stage2_max_days, 10),
        stage1_rate: Number(form.stage1_rate) / 100,
        stage2_rate: Number(form.stage2_rate) / 100,
        stage3_rate: Number(form.stage3_rate) / 100,
        default_rate: Number(form.default_rate) / 100,
      }, { headers });
      toast.success('IFRS9 inputs saved — run the IFRS9 calculation to apply them');
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save IFRS9 inputs');
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (loanNumber, inDefault, reason) => {
    try {
      setMarking(true);
      await axios.put(`${API_URL}/api/lms/loans/${encodeURIComponent(loanNumber)}/default`,
        { in_default: inDefault, reason: reason || null }, { headers });
      toast.success(inDefault ? `Loan ${loanNumber} marked in default` : `Loan ${loanNumber} no longer in default`);
      setNewDefault({ loan_number: '', reason: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update default status');
    } finally {
      setMarking(false);
    }
  };

  if (!settings) return null;

  const stages = [
    { n: 1, name: 'Performing', days: `0-${settings.stage1_max_days} days past repurchase`, rate: settings.stage1_rate, color: 'green', note: '12-month expected loss' },
    { n: 2, name: 'Underperforming', days: `${settings.stage1_max_days + 1}-${settings.stage2_max_days} days past repurchase`, rate: settings.stage2_rate, color: 'amber', note: 'Lifetime expected loss' },
    { n: 3, name: 'Non-performing', days: `${settings.stage2_max_days}+ days past repurchase`, rate: settings.stage3_rate, color: 'red', note: `In default: ${pct(settings.default_rate)}` },
  ];
  const field = (key, label, suffix) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-1">
        <Input type="number" step="any" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`ifrs9-${key}`} />
        <span className="text-xs text-[#6C757D]">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 mb-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#6C757D]">
          IFRS9 inputs{settings.updated_at ? ` — last changed ${String(settings.updated_at).slice(0, 10)} by ${settings.updated_by}` : ' (defaults)'}
        </p>
        {!editing && (
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-ifrs9-settings">
            <Pencil className="h-3 w-3 mr-1" /> Edit inputs
          </Button>
        )}
      </div>

      {editing ? (
        <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {field('stage1_max_days', 'Stage 1 up to', 'days')}
            {field('stage2_max_days', 'Stage 2 up to', 'days')}
            <div />
            {field('stage1_rate', 'Stage 1 rate', '%')}
            {field('stage2_rate', 'Stage 2 rate', '%')}
            {field('stage3_rate', 'Stage 3 rate', '%')}
            {field('default_rate', 'In default rate', '%')}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-ifrs9-settings">
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stages.map((s) => {
            const c = STAGE_CLASSES[s.color];
            return (
              <div key={s.n} className={`${c.box} p-4 rounded-lg border`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={c.badge}>Stage {s.n}</Badge>
                  <span className={`text-sm font-medium ${c.title}`}>{s.name}</span>
                </div>
                <p className={`text-xs ${c.text} mb-2`}>{s.days}</p>
                <div className={`text-lg font-bold ${c.big}`}>{pct(s.rate)} ECL</div>
                <p className={`text-xs ${c.text}`}>{s.note}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="h-4 w-4 text-red-600" />
          <h4 className="font-medium text-sm">Loans in default</h4>
        </div>
        <p className="text-xs text-[#6C757D] mb-3">
          Marked loans go to Stage 3 at the in-default rate and make up PAR30 on the admin dashboard. Uploads never change this list.
        </p>
        <div className="flex flex-col md:flex-row gap-2 mb-3">
          <Input placeholder="Loan number, e.g. 2723" value={newDefault.loan_number}
            onChange={(e) => setNewDefault({ ...newDefault, loan_number: e.target.value })} className="md:w-48" data-testid="default-loan-number" />
          <Input placeholder="Reason (optional)" value={newDefault.reason}
            onChange={(e) => setNewDefault({ ...newDefault, reason: e.target.value })} />
          <Button size="sm" variant="destructive" disabled={marking || !newDefault.loan_number.trim()}
            onClick={() => setDefault(newDefault.loan_number.trim(), true, newDefault.reason)} data-testid="mark-default-btn">
            Mark in default
          </Button>
        </div>
        {defaults.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loan</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Marked</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {defaults.map((d) => (
                <TableRow key={d.loan_number}>
                  <TableCell className="font-medium">{d.loan_number}</TableCell>
                  <TableCell>{d.client_id}</TableCell>
                  <TableCell className="text-right">{formatCurrency(d.exposure)}</TableCell>
                  <TableCell className="text-xs">{d.reason || '-'}</TableCell>
                  <TableCell className="text-xs">{String(d.updated_at || '').slice(0, 10)} {d.updated_by}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={marking} onClick={() => setDefault(d.loan_number, false)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-[#6C757D]">No loans are marked in default.</p>
        )}
      </div>
    </div>
  );
}
